import { ReactNode } from "react";
import { DataKeys } from "../../types";
import { useLang } from "../Context";
import { StaggerInView, StaggerInViewItem } from "../tools/Animations";
import { HealthSection } from "../health/HealthSection";
import { getLocalized, HealthLang } from "../health/healthTypes";
import { healthCol, healthSplitLg } from "../health/healthLayout";
import {
    EDUCATION_PROBLEM_BAND,
    healthBody,
    healthCardTitle,
    healthSectionEyebrow,
} from "../health/healthVisualStyles";

const challengeIcons: Record<string, ReactNode> = {
    "guided-practice": (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
            <circle cx="12" cy="8" r="3" />
            <path d="M6.5 18.5c1.6-2 3.6-3 5.5-3s4 1 5.5 3" strokeLinecap="round" />
        </svg>
    ),
    feedback: (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
            <path d="M5 7h14" strokeLinecap="round" />
            <path d="M5 11h10" strokeLinecap="round" />
            <path d="M5 15h8" strokeLinecap="round" />
            <path d="M6 19l-2 2v-4" strokeLinejoin="round" />
        </svg>
    ),
    quality: (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
            <path d="M4 20V8l8-4 8 4v12" strokeLinejoin="round" />
            <path d="M9 20v-6h6v6" strokeLinejoin="round" />
            <path d="M9 10h6" strokeLinecap="round" />
        </svg>
    ),
    cases: (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
            <circle cx="12" cy="12" r="7" />
            <path d="M12 8v4l2.5 2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
};

interface EducationProblemSectionProps {
    data: DataKeys;
}

export function EducationProblemSection({ data }: EducationProblemSectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const cards: DataKeys[] = data?.cards ?? [];
    const sectionId = "problem";

    return (
        <HealthSection
            id={sectionId}
            ariaLabelledBy={`${sectionId}-title`}
            compact
            style={{ backgroundColor: EDUCATION_PROBLEM_BAND }}
        >
            <div className="grid w-full min-w-0 grid-cols-1 gap-8 lg:grid-cols-[minmax(0,0.34fr)_minmax(0,0.66fr)] lg:items-start lg:gap-10">
                <div className={`${healthCol} min-w-0 text-left [text-align:left]`}>
                    <p className={healthSectionEyebrow}>
                        {getLocalized(data?.eyebrow, hLang)}
                    </p>
                    <h2
                        id={`${sectionId}-title`}
                        className="mt-3 text-left text-xl font-semibold leading-snug text-grey-900 [text-align:left] lg:text-[1.65rem] lg:leading-[1.25]"
                    >
                        {getLocalized(data?.title, hLang)}
                    </h2>
                    <p className={`${healthBody} mt-4 max-w-[22rem] text-grey-500`}>
                        {getLocalized(data?.intro, hLang)}
                    </p>
                </div>

                <div className="min-w-0 overflow-hidden rounded-[1.75rem] border border-grey-100/90 bg-white shadow-[0_14px_34px_rgba(16,24,40,0.06)]">
                        <StaggerInView stagger={0.06}>
                            <ul className="m-0 list-none divide-y divide-grey-100 p-0">
                                {cards.map((card, index) => {
                                    const iconKey = (card.id as string) ?? "guided-practice";

                                    return (
                                        <StaggerInViewItem key={(card.id as string) ?? index} direction="bottom">
                                            <li className="px-4 py-5 text-left [text-align:left] sm:px-6 sm:py-6">
                                                <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-start lg:gap-5">
                                                    <div className="flex shrink-0 items-center gap-3 lg:gap-4">
                                                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-500">
                                                            {challengeIcons[iconKey] ?? challengeIcons["guided-practice"]}
                                                        </span>
                                                        <span className="text-left text-sm font-bold tabular-nums text-teal-500 [text-align:left]">
                                                            {String(index + 1).padStart(2, "0")}
                                                        </span>
                                                        <span
                                                            className="hidden h-10 w-px shrink-0 bg-grey-200 lg:block"
                                                            aria-hidden="true"
                                                        />
                                                    </div>
                                                    <div className="grid min-w-0 flex-1 gap-1.5 text-left [text-align:left] lg:grid-cols-[minmax(0,0.38fr)_minmax(0,1fr)] lg:items-start lg:gap-6">
                                                        <h3
                                                            className={`${healthCardTitle} text-left text-base font-semibold leading-snug text-grey-900 [text-align:left] lg:text-[1.05rem]`}
                                                        >
                                                            {getLocalized(card.title, hLang)}
                                                        </h3>
                                                        <p className="text-left text-sm leading-relaxed text-grey-500 [text-align:left] lg:pt-0.5">
                                                            {getLocalized(card.text, hLang)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </li>
                                        </StaggerInViewItem>
                                    );
                                })}
                            </ul>
                        </StaggerInView>
                    </div>
            </div>
        </HealthSection>
    );
}
